// Generic field renderer + change handler, ported from renderField /
// attachFieldHandlers (source lines 4463-4600). The source called
// updateVisitField() per keystroke-committed field; here every change PUTs
// the whole (small) section object to the clinical-data service, since the
// new API is section-level, not field-level - a deliberate simplification
// (see docs/DECISIONS.md) traded for much simpler state management.
import { escapeHtml } from "./dom.js";

export interface FieldOpts {
  unit?: string;
  choices?: Array<string | { code?: string; key?: string; value?: string; label?: string }>;
  rows?: number;
  attr?: string;
  required?: boolean;
}

export function renderField(label: string, key: string, type: string, value: unknown, writable: boolean, opts: FieldOpts = {}): string {
  const id = `f_${key.replace(/\./g, "_")}`;
  const disabled = writable ? "" : "disabled";
  const dataAttr = `data-fkey="${escapeHtml(key)}"`;
  const labelHtml = escapeHtml(label) + (opts.required ? ` <span class="required-mark">*</span>` : "");

  if (type === "select" && opts.choices) {
    const optsHtml = opts.choices
      .map((c) => {
        const v = typeof c === "object" ? c.code ?? c.key ?? c.value ?? "" : c;
        const lbl = typeof c === "object" ? c.label ?? c.code ?? c.key ?? "" : c;
        return `<option value="${escapeHtml(v)}" ${value === v ? "selected" : ""}>${escapeHtml(lbl)}</option>`;
      })
      .join("");
    return `<div class="form-group"><label for="${id}">${labelHtml}</label><select id="${id}" ${dataAttr} ${disabled}><option value="">—</option>${optsHtml}</select></div>`;
  }
  if (type === "textarea") {
    return `<div class="form-group"><label for="${id}">${labelHtml}</label><textarea id="${id}" ${dataAttr} ${disabled} rows="${opts.rows || 2}">${escapeHtml(value ?? "")}</textarea></div>`;
  }
  if (type === "checkbox") {
    return `<div class="form-group"><label class="cbx"><input type="checkbox" id="${id}" ${dataAttr} ${disabled} ${value ? "checked" : ""}><span>${labelHtml}</span></label></div>`;
  }
  return `<div class="form-group"><label for="${id}">${labelHtml}${opts.unit ? ` <span style="color:var(--text-muted);font-weight:normal">(${escapeHtml(opts.unit)})</span>` : ""}</label><input type="${type}" id="${id}" value="${escapeHtml(value === null || value === undefined ? "" : (value as string))}" ${dataAttr} ${disabled} ${opts.attr || ""}></div>`;
}

// Reads the current value of every data-fkey input/select/textarea within
// `root` into a flat object (dot-path keys supported, e.g. "target.diameter").
export function readFields(root: ParentNode): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  root.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[data-fkey]").forEach((el) => {
    const key = el.dataset.fkey!;
    let val: unknown;
    if (el instanceof HTMLInputElement && el.type === "checkbox") val = el.checked;
    else if (el instanceof HTMLInputElement && el.type === "number") {
      const raw = el.value.replace(",", ".");
      val = raw === "" ? null : Number(raw);
      if (typeof val === "number" && Number.isNaN(val)) val = null;
    } else val = el.value;
    out[key] = val;
  });
  return out;
}

// Attaches a debounced-by-change listener that re-runs `onChange` with the
// full current field set every time any field changes (source's per-field
// updateVisitField, adapted to this API's section-level PUT).
export function attachChangeHandler(root: ParentNode, onChange: () => void): void {
  root.querySelectorAll<HTMLElement>("[data-fkey]").forEach((el) => {
    el.addEventListener("change", onChange);
  });
}
